import { useCallback, useEffect, useState } from 'react';
import { getGroupAnalysisEvidence, getGroupDashboard, getGroupDashboardTrend, getGroupStaffRanking, listGroupAnalyses } from '../../api/adminApi.js';
import type {
  GroupAnalysisOverviewRow,
  GroupAnalysisView,
  GroupDashboardDailyPoint,
  GroupStaffRankingResponse,
  GroupDashboardChurnItem,
  GroupDashboardEvidenceRule,
  GroupDashboardMissedItem,
  GroupDashboardNightItem,
  GroupDashboardSlowItem,
  GroupDashboardTotals,
} from '../../api/types.js';
import { EvidenceSheet, type EvidenceSheetData } from '../analysis/EvidenceSheet.js';
import { complianceLabel, dealSignalLabel, formatDateTime, intentLabel, lifecycleStageLabel, moneyRangeLabel, moneyLabel, pipelineValueLabel, secondsLabel, shareLabel, TIER_NAMES, tierLabel } from '../analysis/labels.js';
import { MetricList } from './MetricList.js';
import '../../styles/dashboard.css';

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];
const RANK_PREVIEW = 10;

/** 风险单卡副行：按类型拆分（只列有数的类型，按待核实降序）。 */
function riskTypeLine(risk: GroupDashboardTotals['risk']): string {
  const parts = Object.entries(risk.byType)
    .map(([type, counts]) => ({ type, total: counts.pending + counts.confirmed + counts.falsePositive, pending: counts.pending }))
    .filter((item) => item.total > 0)
    .sort((left, right) => right.pending - left.pending)
    .map((item) => `${complianceLabel(item.type)} ${item.pending}`);
  return parts.length > 0 ? parts.join(' · ') : '无风险单';
}

/**
 * 老板看板（admin 全量；sales 只见绑定工作号、金额字段由后端脱敏为 null 显示“—”）：四张可点卡 + 流失明细 + AI 接待 + 三张“出事名单”。
 * 全部数字来自 GET /admin/api/group-dashboard 与 /:metric/items，本页不做任何口径计算；
 * 点卡跳到智能分析页对应页签。没有“让 AI 去接”按钮（分析不发送）。
 */
export function DashboardConsole({ isAdmin, onToast, onOpenView }: {
  isAdmin: boolean;
  onToast(message: string, type?: 'success' | 'error'): void;
  onOpenView(view: GroupAnalysisView, params?: Record<string, string>): void;
}) {
  const [days, setDays] = useState<Period>(30);
  const [totals, setTotals] = useState<GroupDashboardTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [evidence, setEvidence] = useState<EvidenceSheetData | null>(null);
  const [rank, setRank] = useState<GroupAnalysisOverviewRow[]>([]);
  const [rankTotal, setRankTotal] = useState(0);
  const [staff, setStaff] = useState<GroupStaffRankingResponse | null>(null);
  const [staffExpanded, setStaffExpanded] = useState(false);
  const [trend, setTrend] = useState<GroupDashboardDailyPoint[]>([]);
  const [trendMetric, setTrendMetric] = useState<'dormant1' | 'dormant7' | 'dormant30' | 'dormant90'>('dormant30');
  const [trendGrain, setTrendGrain] = useState<'day' | 'week' | 'month'>('day');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTotals(await getGroupDashboard(days));
    } catch (error) {
      onToast(error instanceof Error ? error.message : '看板加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [days, onToast]);

  useEffect(() => { void load(); }, [load]);

  // 销售行为排行与沉睡趋势：各自独立请求，失败只影响自己的区块。
  useEffect(() => {
    let cancelled = false;
    getGroupStaffRanking(days).then(
      (response) => { if (!cancelled) setStaff(response); },
      (error: unknown) => { if (!cancelled) onToast(error instanceof Error ? error.message : '销售行为排行加载失败', 'error'); },
    );
    getGroupDashboardTrend(180).then(
      (response) => { if (!cancelled) setTrend(response.points); },
      () => { if (!cancelled) setTrend([]); },
    );
    return () => { cancelled = true; };
  }, [days, onToast]);

  // 看板内嵌排行榜：与分析页同一接口（档位优先，前 10），点“查看全部”进分析页。
  useEffect(() => {
    let cancelled = false;
    listGroupAnalyses({ sort: 'tier_desc', limit: RANK_PREVIEW, days }).then(
      (response) => { if (!cancelled) { setRank(response.rows); setRankTotal(response.pagination.total); } },
      (error: unknown) => { if (!cancelled) onToast(error instanceof Error ? error.message : '排行榜加载失败', 'error'); },
    );
    return () => { cancelled = true; };
  }, [days, onToast]);

  const openEvidence = useCallback(async (item: { conversationId: number; clusterName: string | null }, messageId: number, rule: GroupDashboardEvidenceRule) => {
    const title = `${item.clusterName || `群 ${item.conversationId}`} · 消息 #${messageId}`;
    setEvidence({ title, anchorMessageId: messageId, quote: null, verdict: null, messages: [], loading: true });
    try {
      const response = await getGroupAnalysisEvidence(item.conversationId, messageId, rule);
      setEvidence((current) => current?.anchorMessageId === messageId
        ? { title, anchorMessageId: messageId, quote: null, verdict: response.verdict.text, messages: response.messages, loading: false }
        : current);
    } catch (error) {
      const message = error instanceof Error ? error.message : '证据加载失败';
      setEvidence((current) => current?.anchorMessageId === messageId ? { ...current, loading: false, error: message } : current);
    }
  }, []);

  return (
    <main className="dashboard-console sr">
      <header className="dashboard-header">
        <div>
          <h1>老板看板</h1>
          <p>客户群经营一眼看全：谁该跟、谁在丢、谁出事、钱在哪 · 数字可点开看名单与原话{totals ? ` · 数据截至 ${formatDateTime(totals.generatedAt)}` : ''}</p>
        </div>
        <div className="dashboard-period" role="group" aria-label="周期">
          {PERIODS.map((period) => (
            <button key={period} type="button" className={period === days ? 'active' : ''} onClick={() => setDays(period)}>近 {period} 天</button>
          ))}
          <button type="button" className="dashboard-refresh" onClick={() => void load()} disabled={loading}>刷新</button>
        </div>
      </header>

      <section className="dashboard-cards" aria-label="看板卡片">
        <button type="button" className="dashboard-card card strong metric clk" onClick={() => onOpenView('all')}>
          <span className="go" aria-hidden="true">查看 →</span>
          <span className="dashboard-card-title">客户档案 <span className="q" title="已分析 / 适用客户群；待更新表示快照落后于最新消息">?</span></span>
          <strong>{totals ? `${totals.analyzedGroups} / ${totals.groups}` : '—'}</strong>
          <small>已分析 / 适用 · 待更新 {totals?.staleGroups ?? '—'}</small>
        </button>
        <button type="button" className="dashboard-card card strong metric clk warn" onClick={() => onOpenView('risk')}>
          <span className="go" aria-hidden="true">查看 →</span>
          <span className="dashboard-card-title">风险单 <span className="q" title="待核实风险单条数，按类型汇总">?</span></span>
          <strong>{totals ? totals.risk.pending : '—'}</strong>
          <small>待核实 · 已确认 {totals?.risk.confirmed ?? '—'} · 误报 {totals?.risk.falsePositive ?? '—'}{totals ? ` · ${riskTypeLine(totals.risk)}` : ''}</small>
        </button>
        <button type="button" className="dashboard-card card strong metric clk" onClick={() => onOpenView('today')}>
          <span className="go" aria-hidden="true">查看 →</span>
          <span className="dashboard-card-title">今日跟进 <span className="q" title="今天需要跟进的客户群；分类可能交叉">?</span></span>
          <strong>{totals ? totals.todayFollowUps : '—'}</strong>
          <small>
            意向客户 {totals?.intentGroups ?? '—'} · 漏跟 {totals?.missedGroups ?? '—'} · 该回访 {totals?.revisitGroups ?? '—'} · 成交待核 {totals?.dealPendingGroups ?? '—'} · 超 30 分钟 {totals?.slowReplies ?? '—'} 条 · 夜里没人接 {totals?.nightMissed ?? '—'} 条
          </small>
        </button>
        <button type="button" className="dashboard-card card strong metric clk money" onClick={() => onOpenView('pipeline')}>
          <span className="go" aria-hidden="true">查看 →</span>
          <span className="dashboard-card-title">预计损失 <span className="q" title="根据流失群的在途金额区间估算；无权限或未知显示横线">?</span></span>
          <strong>{totals ? moneyRangeLabel(totals.loss.min, totals.loss.max) : '—'}</strong>
          <small>
            在途 {totals ? moneyRangeLabel(totals.pipeline.min, totals.pipeline.max) : '—'} · {totals?.pipeline.groups ?? '—'} 群 · 流失 {totals?.churnGroups ?? '—'} 群 · 预计损失 {totals ? moneyRangeLabel(totals.loss.min, totals.loss.max) : '—'} · AI 节省 {totals ? moneyLabel(totals.aiReception.laborCostEquivalent) : '—'}
          </small>
        </button>
      </section>

      <MetricList
        key={`churn-${days}`}
        metric="churn"
        days={days}
        title="流失明细"
        hint={`最后一条客户消息距今 ≥ 180 天且窗口内我方没回；共 ${totals?.churnGroups ?? '—'} 群`}
        columns={['客户群', '最后一句', '沉默', '判定', '原话']}
        renderRow={(item) => {
          const row = item as GroupDashboardChurnItem;
          return (
            <tr key={row.conversationId}>
              <td><strong>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
              <td>{formatDateTime(row.lastMessageAt)}</td>
              <td>{row.silentDays} 天</td>
              <td>{row.verdict}</td>
              <td><button type="button" className="button ghost" onClick={() => void openEvidence(row, row.lastCustomerMessageId, 'missed')}>看原话</button></td>
            </tr>
          );
        }}
        onToast={onToast}
      />

      <h2 className="dashboard-section-title">待处理</h2>
      <div className="dashboard-grid">
        <MetricList
          key={`missed-${days}`}
          metric="missed"
          days={days}
          title="漏跟"
          hint="最后一句是客户说的，之后 ≥ 2 小时没人回（含历史）"
          columns={['客户群', '客户最后一句', '判定', '原话']}
          renderRow={(item) => {
            const row = item as GroupDashboardMissedItem;
            return (
              <tr key={row.conversationId}>
                <td><strong>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
                <td>{formatDateTime(row.messageAt)}</td>
                <td>{row.verdict}</td>
                <td><button type="button" className="button ghost" onClick={() => void openEvidence(row, row.messageId, 'missed')}>看原话</button></td>
              </tr>
            );
          }}
          onToast={onToast}
        />
        <MetricList
          key={`slow-${days}`}
          metric="slow"
          days={days}
          title="超 30 分钟才回"
          hint="周期内客户消息到我方首条回复间隔 > 30 分钟，按群聚合"
          columns={['客户群', '次数', '最近一次', '判定', '原话']}
          renderRow={(item) => {
            const row = item as GroupDashboardSlowItem;
            return (
              <tr key={row.conversationId}>
                <td><strong>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
                <td>{row.slowCount}</td>
                <td>{formatDateTime(row.latestMessageAt)}</td>
                <td>{row.verdict}</td>
                <td><button type="button" className="button ghost" onClick={() => void openEvidence(row, row.latestMessageId, 'slow')}>看原话</button></td>
              </tr>
            );
          }}
          onToast={onToast}
        />
        <MetricList
          key={`night-${days}`}
          metric="night"
          days={days}
          title="夜里没人接"
          hint="21:00–06:00 的客户消息到次日 06:00 前没人回，按群聚合"
          columns={['客户群', '次数', '最近一次', '判定', '原话']}
          renderRow={(item) => {
            const row = item as GroupDashboardNightItem;
            return (
              <tr key={row.conversationId}>
                <td><strong>{row.clusterName || `群 ${row.conversationId}`}</strong></td>
                <td>{row.nightCount}</td>
                <td>{formatDateTime(row.latestMessageAt)} · {row.latestLocalTime}</td>
                <td>{row.verdict}</td>
                <td><button type="button" className="button ghost" onClick={() => void openEvidence(row, row.latestMessageId, 'night')}>看原话</button></td>
              </tr>
            );
          }}
          onToast={onToast}
        />
      </div>

      <section className="dashboard-block" aria-label="客户资产">
        <header>
          <h2>客户资产 <span className="dashboard-count">{totals?.assets.applicable ?? '—'} 群</span></h2>
          <p>客户分层 · 点档位看名单 · 有效客户 = 适用群（工作号名下、有客户消息、非内部/设计师群）</p>
        </header>
        <div className="dashboard-assets">
          <div className="dashboard-stats dashboard-stats-4">
            <div><span>有效客户</span><strong>{totals?.assets.applicable ?? '—'}</strong><small>已分析 {totals?.assets.analyzed ?? '—'}</small></div>
            <div><span>近 30 天活跃</span><strong>{totals?.assets.active30 ?? '—'}</strong><small>{totals && totals.assets.applicable > 0 ? `${((totals.assets.active30 / totals.assets.applicable) * 100).toFixed(1)}%` : ''}</small></div>
            <div><span>沉睡 30 天+</span><strong>{totals?.assets.dormant ?? '—'}</strong><small>{totals && totals.assets.applicable > 0 ? `${((totals.assets.dormant / totals.assets.applicable) * 100).toFixed(1)}%` : ''}</small></div>
            <div><span>流失</span><strong>{totals?.assets.churn ?? '—'}</strong><small>{totals && totals.assets.applicable > 0 ? `${((totals.assets.churn / totals.assets.applicable) * 100).toFixed(1)}%` : ''}</small></div>
          </div>
          <div className="dashboard-tiers">
            {(['A', 'B', 'C', 'D', 'none'] as const).map((tier) => (
              <button key={tier} type="button" className="dashboard-tier" onClick={() => onOpenView('all', tier === 'none' ? {} : { tier })}>
                <span className={`analysis-tier tier-${tier}`}>{tier === 'none' ? '未评级' : `${tier} · ${TIER_NAMES[tier]}`}</span>
                <strong>{totals?.assets.byTier[tier] ?? '—'}</strong>
                <small>{totals && totals.assets.applicable > 0 ? `${((totals.assets.byTier[tier] / totals.assets.applicable) * 100).toFixed(1)}%` : ''}</small>
              </button>
            ))}
          </div>

        </div>
      <TrendBlock points={trend} metric={trendMetric} grain={trendGrain} onMetric={setTrendMetric} onGrain={setTrendGrain} />

      </section>

      <section className="dashboard-block dashboard-reception" aria-label="AI 接待">
        <header>
          <h2>AI 接待</h2>
          <p>人工口径 = 内部名单账号在群里的发言；工作号机主手机端发言不入账</p>
        </header>
        <div className="dashboard-stats">
          <div><span>AI 参与会话</span><strong>{totals?.aiReception.aiSessions ?? '—'} 个</strong><small>AI 至少回复过 1 条的群</small></div>
          <div><span>AI 回复</span><strong>{totals?.aiReception.aiReplyCount ?? '—'} 条</strong></div>
          <div><span>人工回复</span><strong>{totals?.aiReception.manualReplyCount ?? '—'} 条</strong></div>
          <div><span>首响中位 · AI</span><strong>{totals ? secondsLabel(totals.aiReception.firstResponseMedianSeconds.ai) : '—'}</strong></div>
          <div><span>首响中位 · 人工</span><strong>{totals ? secondsLabel(totals.aiReception.firstResponseMedianSeconds.manual) : '—'}</strong></div>
          <div>
            <span>转人工</span>
            <strong>{totals?.aiReception.handoffCount ?? '—'} 次</strong>
            <small>{totals ? `客户要求 ${totals.aiReception.handoff.customerRequested} · 规则触发 ${totals.aiReception.handoff.ruleTriggered}` : ''}</small>
          </div>
          <div>
            <span>转人工待处理</span>
            <strong>{totals?.aiReception.handoff.pending ?? '—'} 个</strong>
            <small>{totals ? `已处理 ${totals.aiReception.handoff.resolved} · 处理中位 ${secondsLabel(totals.aiReception.handoff.medianResolveSeconds)}` : ''}</small>
          </div>
          <div>
            <span>夜间接待占比</span>
            <strong>{totals ? shareLabel(totals.aiReception.night.aiShare) : '—'}</strong>
            <small>{totals ? `夜间客户消息 ${totals.aiReception.night.customerMessages} 条 · AI 首响 ${totals.aiReception.night.aiFirstResponse}` : ''}</small>
          </div>
          <div>
            <span>折合人工</span>
            <strong>{totals ? `${totals.aiReception.laborHoursEquivalent} 小时` : '—'}</strong>
            <small>{totals ? `≈ ${moneyLabel(totals.aiReception.laborCostEquivalent)}` : ''}</small>
          </div>
        </div>
      </section>

      <section className="dashboard-block" aria-label="成交信号">
        <header>
          <h2>成交信号 <span className="dashboard-count">{totals?.dealSignals.groups ?? '—'} 群</span></h2>
          <p>AI 从群聊识别，供核对：交易在微信或线下完成，系统不记录金额；每条信号在详情「需求与订单」可看原话</p>
        </header>
        <div className="dashboard-stats">
          {totals ? Object.entries(totals.dealSignals.byType).sort((left, right) => right[1] - left[1]).map(([type, count]) => (
            <div key={type}><span>{dealSignalLabel(type)}</span><strong>{count} 条</strong></div>
          )) : null}
          {totals && Object.keys(totals.dealSignals.byType).length === 0 ? <div><span>暂无</span><strong>0</strong></div> : null}
        </div>
      </section>

      <section className="dashboard-block" aria-label="销售行为排行">
        <header>
          <h2>销售行为排行 <span className="dashboard-count">{staff?.accounts ?? '—'} 个账号</span></h2>
          <p>近 {days} 天 · 内部名单账号（人工客服个人号）· 最差在前 · 群归属 = 周期内最近一条人工回复的账号 · 每格点开看名单</p>
        </header>
        {staff ? (
          <div className="dashboard-table-wrap">
            <table>
              <thead><tr><th>账号</th><th>归属群</th><th>首响中位</th><th>超 30 分钟未回</th><th>夜间未接</th><th>跟丢</th><th>承诺到期未跟</th><th>意向→成交</th><th>风险单</th></tr></thead>
              <tbody>
                {(staffExpanded ? staff.rows : staff.rows.slice(0, 8)).map((row) => (
                  <tr key={row.account}>
                    <td><strong>{row.displayName ?? maskWechatId(row.account)}</strong></td>
                    <td>{row.groups}</td>
                    <td className={row.firstResponseMedianSeconds !== null && row.firstResponseMedianSeconds > 1800 ? 'bad' : ''}>{secondsLabel(row.firstResponseMedianSeconds)}</td>
                    <td className={row.slowReplies >= 10 ? 'bad' : ''}>{row.slowReplies}</td>
                    <td className={row.nightMissed >= 3 ? 'bad' : ''}>{row.nightMissed}</td>
                    <td className={row.missedGroups >= 3 ? 'bad' : ''}>{row.missedGroups}</td>
                    <td className={row.overdueGroups >= 2 ? 'bad' : ''}>{row.overdueGroups}</td>
                    <td>{row.intentGroups > 0 ? `${row.dealGroups}/${row.intentGroups}（${((row.dealGroups / row.intentGroups) * 100).toFixed(0)}%）` : '—'}</td>
                    <td className={row.riskFlags >= 1 ? 'bad' : ''}>{row.riskFlags}</td>
                  </tr>
                ))}
                {staff.teamAverage ? (
                  <tr className="dashboard-staff-average">
                    <td><strong>{isAdmin ? '团队均值' : '团队均值（你只能看到均值，看不到排名）'}</strong></td>
                    <td>{staff.teamAverage.groups}</td>
                    <td>{secondsLabel(staff.teamAverage.firstResponseMedianSeconds)}</td>
                    <td>{staff.teamAverage.slowReplies}</td>
                    <td>{staff.teamAverage.nightMissed}</td>
                    <td>{staff.teamAverage.missedGroups}</td>
                    <td>{staff.teamAverage.overdueGroups}</td>
                    <td>{staff.teamAverage.intentGroups > 0 ? `${staff.teamAverage.dealGroups}/${staff.teamAverage.intentGroups}` : '—'}</td>
                    <td>{staff.teamAverage.riskFlags}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
            {staff.rows.length === 0 && !staff.teamAverage ? <div className="analysis-empty">周期内没有人工回复记录</div> : null}
            {staff.rows.length > 8 ? <footer><button type="button" className="button ghost" onClick={() => setStaffExpanded((current) => !current)}>{staffExpanded ? '收起' : `展开全部 ${staff.rows.length} 个账号`}</button></footer> : null}
          </div>
        ) : <div className="analysis-empty">加载中…</div>}
      </section>

      <section className="dashboard-block" aria-label="客户排行榜">
        <header>
          <h2>客户排行榜 <span className="dashboard-count">{rankTotal}</span></h2>
          <p>档位 · 客群 · 在途金额 · 意向 · 点行进分析页 <button type="button" className="dashboard-link" onClick={() => onOpenView('all')}>查看全部 →</button></p>
        </header>
        <div className="dashboard-table-wrap">
          <table>
            <thead><tr><th>档位</th><th>客户群</th><th>客群</th><th>在途金额</th><th>意向</th><th>成交信号</th><th>最后互动</th><th>AI 一句话</th></tr></thead>
            <tbody>
              {rank.map((row) => (
                <tr key={row.conversationId} className="dashboard-rank-row" onClick={() => onOpenView('all')}>
                  <td>{row.snapshot ? <span className={`analysis-tier tier-${row.snapshot.tier ?? 'none'}`}>{tierLabel(row.snapshot.tier)}</span> : '-'}</td>
                  <td><strong>{row.clusterName || `群 ${row.clusterId}`}</strong></td>
                  <td>{lifecycleStageLabel(row.snapshot?.lifecycleStage)}</td>
                  <td>{pipelineValueLabel(row.snapshot?.pipelineValue)}</td>
                  <td>{row.snapshot ? `${intentLabel(row.snapshot.intentLevel)} ${row.snapshot.intentScore ?? ''}` : '-'}</td>
                  <td>{row.snapshot?.dealSignalCount ? `待核 ×${row.snapshot.dealSignalCount}` : '无'}</td>
                  <td>{formatDateTime(row.followUp?.lastMessageAt ?? row.lastMessageAt)}</td>
                  <td className="dashboard-rank-headline">{row.snapshot?.headline ?? '尚未分析'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rank.length === 0 ? <div className="analysis-empty">暂无</div> : null}
        </div>
      </section>

      <footer className="dashboard-footnote">口径：近 {days} 天；人工回复仅统计内部名单账号。金额缺失或无查看权限时显示“—”。</footer>

      {evidence ? <EvidenceSheet data={evidence} onClose={() => setEvidence(null)} /> : null}
    </main>
  );
}

function maskWechatId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 5)}…${id.slice(-4)}` : id;
}

const TREND_METRICS: ReadonlyArray<{ key: 'dormant1' | 'dormant7' | 'dormant30' | 'dormant90'; label: string }> = [
  { key: 'dormant1', label: '≥1 天' },
  { key: 'dormant7', label: '≥7 天' },
  { key: 'dormant30', label: '≥30 天' },
  { key: 'dormant90', label: '≥90 天' },
];

/** 按日/周/月取每个桶的最后一天快照（趋势看“期末存量”，不是求和）。 */
function bucketTrend(points: GroupDashboardDailyPoint[], grain: 'day' | 'week' | 'month'): GroupDashboardDailyPoint[] {
  if (grain === 'day') return points;
  const buckets = new Map<string, GroupDashboardDailyPoint>();
  for (const point of points) {
    const date = new Date(`${point.date}T00:00:00Z`);
    let key: string;
    if (grain === 'month') key = point.date.slice(0, 7);
    else {
      const day = (date.getUTCDay() + 6) % 7;
      const monday = new Date(date.getTime() - day * 86_400_000);
      key = monday.toISOString().slice(0, 10);
    }
    buckets.set(key, point);
  }
  return [...buckets.values()];
}

/** 沉睡趋势：每日快照（预热任务每天落一行），显示期末存量、占比与较上一快照变化；快照从 2026-09-09 起累积。 */
function TrendBlock({ points, metric, grain, onMetric, onGrain }: {
  points: GroupDashboardDailyPoint[];
  metric: 'dormant1' | 'dormant7' | 'dormant30' | 'dormant90';
  grain: 'day' | 'week' | 'month';
  onMetric(metric: 'dormant1' | 'dormant7' | 'dormant30' | 'dormant90'): void;
  onGrain(grain: 'day' | 'week' | 'month'): void;
}) {
  const series = bucketTrend(points, grain);
  const latest = series[series.length - 1];
  const previous = series.length > 1 ? series[series.length - 2] : undefined;
  const max = Math.max(1, ...series.map((point) => point[metric]));
  return (
    <section className="dashboard-trend-section" aria-label="沉睡趋势">
      <header>
        <h2>沉睡趋势</h2>
        <p>
          {latest ? `沉睡 ${TREND_METRICS.find((item) => item.key === metric)?.label} ${latest[metric]} 群 · 占比 ${latest.applicable > 0 ? ((latest[metric] / latest.applicable) * 100).toFixed(1) : '—'}%` : '还没有快照'}
          {latest && previous ? ` · 较上一快照 ${latest[metric] - previous[metric] >= 0 ? '+' : ''}${latest[metric] - previous[metric]}` : ''}
          {' · 每天一份快照，从 2026-09-09 起累积'}
        </p>
      </header>
      <div className="dashboard-period dashboard-trend-controls">
        {TREND_METRICS.map((item) => <button key={item.key} type="button" className={item.key === metric ? 'active' : ''} onClick={() => onMetric(item.key)}>{item.label}</button>)}
        <span className="dashboard-trend-gap" />
        {(['day', 'week', 'month'] as const).map((item) => <button key={item} type="button" className={item === grain ? 'active' : ''} onClick={() => onGrain(item)}>{item === 'day' ? '按日' : item === 'week' ? '按周' : '按月'}</button>)}
      </div>
      {series.length === 0 ? <div className="analysis-empty">第一份快照会在今天的预热任务里生成</div> : (
        <div className="dashboard-trend">
          {series.slice(-45).map((point) => (
            <div key={point.date} className="dashboard-trend-bar" title={`${point.date} · 沉睡 ${point[metric]} / 适用 ${point.applicable} · 活跃 ${point.active30} · 流失 ${point.churn}`}>
              <span style={{ height: `${Math.max(4, (point[metric] / max) * 100)}%` }} />
              <small>{grain === 'month' ? point.date.slice(5, 7) : point.date.slice(5)}</small>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
